// pairingApi — typed fetchers for /v1/locations/:id/{pairing-code,unbind,rebind}.
import { apiFetch } from "../../lib/api-client.js";

export interface IssueCodeResult {
  ok: true;
  code: string;                      // 8 uppercase chars
  expires_at: number;                // epoch ms
  expires_in_seconds: number;
  reused: boolean;
  correlation_id: string | null;
}

export interface UnbindResult {
  ok: true;
  unbound_count: number;
  unbound_uids: string[];
  correlation_id: string | null;
}

export interface RebindResult extends IssueCodeResult {
  unbound_count: number;
  unbound_uids: string[];
}

export class PairingApiError extends Error {
  status: number; code: string; correlationId: string | null; upstreamMessage?: string;
  constructor(status: number, code: string, correlationId: string | null, upstreamMessage?: string) {
    super(code); this.status = status; this.code = code; this.correlationId = correlationId; this.upstreamMessage = upstreamMessage;
  }
}

export function friendlyPairingError(err: unknown): string {
  if (err instanceof PairingApiError) {
    if (err.status === 401) return "登入狀態已逾時，請重新登入後再試。";
    if (err.status === 403) return "您目前的角色不能管理加密巡邏標籤綁定。";
    if (err.code === "location_not_found") return "找不到此地點。";
    if (err.code === "location_not_active") return "此地點已退役，無法管理加密巡邏標籤綁定。";
    if (err.code === "tenant_id_required")
      return "尚未選定操作的單位，請先到「平台 / 租戶」選擇要進入的單位。";
    return `操作失敗（HTTP ${err.status}・code=${err.code}・cid=${err.correlationId ?? "n/a"}）。`;
  }
  return "連線中斷，請檢查網路後再試。";
}

async function asError(r: Response): Promise<PairingApiError> {
  let code = "request_failed"; let cid: string | null = r.headers.get("x-correlation-id"); let msg: string | undefined;
  try {
    const body = (await r.json()) as { error?: string; correlation_id?: string; message?: string };
    code = body.error ?? code; cid = body.correlation_id ?? cid; msg = body.message;
  } catch {}
  return new PairingApiError(r.status, code, cid, msg);
}

function tenantQuery(tenantId: string | null | undefined): string {
  return tenantId ? `?tenant_id=${encodeURIComponent(tenantId)}` : "";
}

export async function issuePairingCode(locationId: string, tenantId: string | null = null): Promise<IssueCodeResult> {
  const r = await apiFetch(
    `/v1/locations/${encodeURIComponent(locationId)}/pairing-code${tenantQuery(tenantId)}`,
    { method: "POST", credentials: "include" },
  );
  if (!r.ok) throw await asError(r);
  return (await r.json()) as IssueCodeResult;
}

export async function unbindLocation(locationId: string, tenantId: string | null = null): Promise<UnbindResult> {
  const r = await apiFetch(
    `/v1/locations/${encodeURIComponent(locationId)}/unbind${tenantQuery(tenantId)}`,
    { method: "POST", credentials: "include" },
  );
  if (!r.ok) throw await asError(r);
  return (await r.json()) as UnbindResult;
}

export async function rebindLocation(locationId: string, tenantId: string | null = null): Promise<RebindResult> {
  const r = await apiFetch(
    `/v1/locations/${encodeURIComponent(locationId)}/rebind${tenantQuery(tenantId)}`,
    { method: "POST", credentials: "include" },
  );
  if (!r.ok) throw await asError(r);
  return (await r.json()) as RebindResult;
}
